import { useState, useRef } from "react";
import MarketingShell from "@/components/MarketingShell";
import {
  Image, Search, Upload, Link2, Trash2,
  Download, Copy, X, File, FileText, Video, Loader2,
} from "lucide-react";
import { toast } from "sonner";

const BRAND_KIT = {
  colors: [
    { name: "Primary Blue", hex: "#2260a8" },
    { name: "Pop Blue", hex: "#0071E3" },
    { name: "Navy", hex: "#0f1e38" },
    { name: "White", hex: "#FFFFFF" },
    { name: "Green", hex: "#1a7a52" },
    { name: "Gray", hex: "#71706b" },
  ],
  typography: "Helvetica Neue / SF Pro / System",
  tagline: "The Future of Practice Management",
};

const DEFAULT_ASSETS = [
  { id: "a1", name: "Enable Healthcare Logo (Blue)", type: "image", format: "PNG", size: "24 KB", uploadedAt: "Mar 15, 2026", category: "logos" },
  { id: "a2", name: "Enable Healthcare Logo (White)", type: "image", format: "PNG", size: "22 KB", uploadedAt: "Mar 15, 2026", category: "logos" },
  { id: "a3", name: "EHI Emblem Badge", type: "image", format: "PNG", size: "8 KB", uploadedAt: "Mar 15, 2026", category: "logos" },
  { id: "a4", name: "Product Dashboard Screenshot", type: "image", format: "PNG", size: "480 KB", uploadedAt: "Mar 20, 2026", category: "images" },
  { id: "a5", name: "AI Charting Feature Hero", type: "image", format: "JPG", size: "320 KB", uploadedAt: "Mar 25, 2026", category: "images" },
  { id: "a6", name: "CCM Revenue Infographic", type: "image", format: "PNG", size: "560 KB", uploadedAt: "Mar 28, 2026", category: "images" },
  { id: "a7", name: "Patient Engagement Guide", type: "document", format: "PDF", size: "1.2 MB", uploadedAt: "Mar 22, 2026", category: "documents" },
  { id: "a8", name: "Q1 2026 Product Deck", type: "document", format: "PDF", size: "3.8 MB", uploadedAt: "Mar 30, 2026", category: "documents" },
  { id: "a9", name: "Demo Video Intro", type: "video", format: "MP4", size: "8.2 MB", uploadedAt: "Apr 1, 2026", category: "videos" },
  { id: "a10", name: "Social Post Template", type: "image", format: "PSD", size: "2.1 MB", uploadedAt: "Mar 18, 2026", category: "images" },
  { id: "a11", name: "Email Header Banner", type: "image", format: "PNG", size: "156 KB", uploadedAt: "Mar 25, 2026", category: "images" },
  { id: "a12", name: "Brand Guidelines v3", type: "document", format: "PDF", size: "5.4 MB", uploadedAt: "Mar 10, 2026", category: "documents" },
];

const TYPE_ICONS = { image: Image, document: FileText, video: Video };

export default function BrandLibrary() {
  const [assets, setAssets] = useState(DEFAULT_ASSETS);
  const [filter, setFilter] = useState("all");
  const [search, setSearch] = useState("");
  const [showUpload, setShowUpload] = useState(false);
  const [uploading, setUploading] = useState(false);
  const fileRef = useRef(null);

  const filters = [
    { key: "all", label: "All" },
    { key: "images", label: "Images" },
    { key: "logos", label: "Logos" },
    { key: "documents", label: "Documents" },
    { key: "videos", label: "Videos" },
  ];

  const filtered = assets.filter(a => {
    if (filter !== "all" && a.category !== filter) return false;
    if (search && !a.name.toLowerCase().includes(search.toLowerCase())) return false;
    return true;
  });

  const copyHex = (hex) => {
    navigator.clipboard?.writeText(hex);
    toast.success(`Copied ${hex}`);
  };

  const handleUpload = (files) => {
    if (!files || files.length === 0) return;
    setUploading(true);
    setTimeout(() => {
      const newAssets = Array.from(files).map((f, i) => ({
        id: `a_${Date.now()}_${i}`,
        name: f.name,
        type: f.type.startsWith("image") ? "image" : f.type.includes("pdf") ? "document" : f.type.startsWith("video") ? "video" : "document",
        format: f.name.split(".").pop().toUpperCase(),
        size: f.size > 1024 * 1024 ? `${(f.size / (1024 * 1024)).toFixed(1)} MB` : `${(f.size / 1024).toFixed(0)} KB`,
        uploadedAt: new Date().toLocaleDateString("en-US", { month: "short", day: "numeric", year: "numeric" }),
        category: f.type.startsWith("image") ? "images" : f.type.startsWith("video") ? "videos" : "documents",
      }));
      setAssets([...newAssets, ...assets]);
      setUploading(false);
      setShowUpload(false);
      toast.success(`${files.length} file${files.length > 1 ? "s" : ""} uploaded`);
    }, 1500);
  };

  return (
    <MarketingShell
      title="Brand Library"
      subtitle={`${assets.length} assets`}
      actions={
        <>
          <button className="btn"><Link2 style={{ width: 13, height: 13 }} /> Add from URL</button>
          <button className="btn btn-primary" onClick={() => setShowUpload(true)} data-testid="upload-btn">
            <Upload style={{ width: 13, height: 13 }} /> Upload files
          </button>
        </>
      }
    >
    <div data-testid="brand-library">
      {/* Brand Kit */}
      <div className="mkt-card mkt-card-pad" style={{ marginBottom: 18 }}>
        <h3 className="card-title" style={{ fontSize: 13, marginBottom: 12 }}>Brand Kit — Enable Healthcare</h3>
        <div style={{ display: "flex", gap: 28, alignItems: "flex-start", flexWrap: "wrap" }}>
          <div>
            <div className="eyebrow" style={{ marginBottom: 6 }}>Colors</div>
            <div style={{ display: "flex", gap: 7 }}>
              {BRAND_KIT.colors.map(c => (
                <button key={c.hex} onClick={() => copyHex(c.hex)} title={`${c.name} — ${c.hex} (click to copy)`}
                  style={{ width: 34, height: 34, borderRadius: 8, background: c.hex, border: c.hex === "#FFFFFF" ? "1px solid var(--border)" : "none", cursor: "pointer", transition: "transform 0.1s", boxShadow: "var(--shadow-sm)" }}
                  onMouseDown={e => e.currentTarget.style.transform = "scale(0.92)"}
                  onMouseUp={e => e.currentTarget.style.transform = "scale(1)"} />
              ))}
            </div>
          </div>
          <div>
            <div className="eyebrow" style={{ marginBottom: 6 }}>Typography</div>
            <div style={{ fontSize: 12, color: "var(--text-secondary)" }}>{BRAND_KIT.typography}</div>
          </div>
          <div>
            <div className="eyebrow" style={{ marginBottom: 6 }}>Tagline</div>
            <div style={{ fontSize: 12, color: "var(--text-secondary)", fontStyle: "italic" }}>"{BRAND_KIT.tagline}"</div>
          </div>
        </div>
      </div>

      {/* Filters */}
      <div style={{ display: "flex", alignItems: "center", gap: 12, marginBottom: 16 }}>
        <div className="mkt-segmented">
          {filters.map(f => (
            <button key={f.key} onClick={() => setFilter(f.key)}
              className={filter === f.key ? "active" : ""}
              data-testid={`filter-${f.key}`}>{f.label}</button>
          ))}
        </div>
        <div style={{ flex: 1 }} />
        <div style={{ position: "relative" }}>
          <Search style={{ position: "absolute", left: 10, top: "50%", transform: "translateY(-50%)", width: 13, height: 13, color: "var(--text-tertiary)" }} />
          <input value={search} onChange={e => setSearch(e.target.value)} placeholder="Search assets…" className="input" style={{ paddingLeft: 30, fontSize: 12, width: 220, height: 30 }} data-testid="search-assets" />
        </div>
      </div>

      {/* Asset Grid */}
      <div style={{ display: "grid", gridTemplateColumns: "repeat(3, 1fr)", gap: 14 }}>
        {filtered.map(asset => {
          const TypeIcon = TYPE_ICONS[asset.type] || File;
          const bgColor = asset.type === "image" ? "var(--blue-soft)" : asset.type === "video" ? "var(--purple-light)" : "var(--warning-soft)";
          const iconColor = asset.type === "image" ? "var(--blue)" : asset.type === "video" ? "var(--purple)" : "var(--warning)";
          return (
            <div key={asset.id} className="mkt-card is-interactive" style={{ overflow: "hidden" }} data-testid={`asset-card-${asset.id}`}>
              <div style={{ height: 120, background: bgColor, display: "flex", alignItems: "center", justifyContent: "center" }}>
                <TypeIcon style={{ width: 28, height: 28, color: iconColor }} />
              </div>
              <div style={{ padding: "10px 14px" }}>
                <div style={{ fontSize: 12.5, fontWeight: 540, color: "var(--text-primary)", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap", marginBottom: 4 }}>{asset.name}</div>
                <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
                  <div style={{ display: "flex", gap: 6, alignItems: "center" }}>
                    <span className="tag tag-gray">{asset.format}</span>
                    <span className="num" style={{ fontSize: 10, color: "var(--text-tertiary)" }}>{asset.size}</span>
                  </div>
                  <span style={{ fontSize: 10, color: "var(--text-tertiary)" }}>{asset.uploadedAt}</span>
                </div>
                <div style={{ display: "flex", gap: 4, marginTop: 8 }}>
                  <button title="Download" style={{ background: "none", border: "none", cursor: "pointer", color: "var(--text-tertiary)", padding: 3 }}><Download style={{ width: 12, height: 12 }} /></button>
                  <button title="Copy URL" onClick={() => toast.success("URL copied")} style={{ background: "none", border: "none", cursor: "pointer", color: "var(--text-tertiary)", padding: 3 }}><Copy style={{ width: 12, height: 12 }} /></button>
                  <button title="Delete" onClick={() => { setAssets(assets.filter(a => a.id !== asset.id)); toast.success("Asset deleted"); }} style={{ background: "none", border: "none", cursor: "pointer", color: "var(--text-tertiary)", padding: 3 }}><Trash2 style={{ width: 12, height: 12 }} /></button>
                </div>
              </div>
            </div>
          );
        })}
      </div>
      {filtered.length === 0 && (
        <div style={{ padding: 40, textAlign: "center", color: "var(--text-tertiary)", fontSize: 13 }}>No assets match your filter</div>
      )}

      {/* Upload Modal */}
      {showUpload && (
        <div style={{ position: "fixed", inset: 0, background: "rgba(0,0,0,0.4)", zIndex: 100, display: "flex", alignItems: "center", justifyContent: "center" }} onClick={() => setShowUpload(false)}>
          <div style={{ background: "var(--surface)", borderRadius: 14, width: 480, padding: "24px 28px", boxShadow: "var(--shadow-float)" }} onClick={e => e.stopPropagation()} data-testid="upload-modal">
            <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 20 }}>
              <h2 className="card-title" style={{ fontSize: 16 }}>Upload files</h2>
              <button onClick={() => setShowUpload(false)} style={{ background: "none", border: "none", cursor: "pointer", color: "var(--text-tertiary)" }}><X style={{ width: 16, height: 16 }} /></button>
            </div>
            <div
              onClick={() => fileRef.current?.click()}
              onDragOver={e => { e.preventDefault(); e.currentTarget.style.borderColor = "var(--blue)"; }}
              onDragLeave={e => { e.currentTarget.style.borderColor = "var(--border)"; }}
              onDrop={e => { e.preventDefault(); handleUpload(e.dataTransfer.files); }}
              style={{ border: "2px dashed var(--border)", borderRadius: 14, padding: "44px 20px", textAlign: "center", cursor: "pointer", transition: "border-color 0.15s, background 0.15s", background: "var(--subtle)" }}
            >
              {uploading ? (
                <Loader2 style={{ width: 28, height: 28, color: "var(--blue)", animation: "spin 1s linear infinite", margin: "0 auto" }} />
              ) : (
                <>
                  <Upload style={{ width: 30, height: 30, color: "var(--text-tertiary)", margin: "0 auto 10px" }} />
                  <p style={{ fontSize: 13, fontWeight: 540, color: "var(--text-primary)", marginBottom: 4 }}>Drag files here or click to browse</p>
                  <p style={{ fontSize: 11, color: "var(--text-tertiary)" }}>JPG, PNG, GIF, SVG, PDF, MP4 · 10MB per file</p>
                </>
              )}
            </div>
            <input ref={fileRef} type="file" multiple accept="image/*,.pdf,.mp4,.svg" style={{ display: "none" }} onChange={e => handleUpload(e.target.files)} />
          </div>
        </div>
      )}
    </div>
    </MarketingShell>
  );
}
